import { useState, useEffect, useRef } from 'react';
import quizApiService from '../services/quizApi';

/**
 * Custom hook for debounced duplicate quiz title validation
 * Checks if a quiz title already exists as the user types
 *
 * @param {string} title - The quiz title to validate
 * @param {number} delay - Debounce delay in milliseconds (default: 500)
 * @param {string} currentQuizId - Optional quiz ID for editing (to exclude from duplicate check)
 * @returns {object} - { isDuplicate, isChecking, error, duplicateQuizId }
 */
export function useDebouncedTitleValidation(title, delay = 500, currentQuizId = null) {
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [duplicateQuizId, setDuplicateQuizId] = useState(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Clear previous abort controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Reset state if title is empty
    if (!title || title.trim().length === 0) {
      setIsDuplicate(false);
      setIsChecking(false);
      setError(null);
      setDuplicateQuizId(null);
      return;
    }

    // Set up debounce timer
    debounceTimerRef.current = setTimeout(async () => {
      setIsChecking(true);
      setError(null);

      try {
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        const response = await quizApiService.checkTitleExists(title.trim());

        if (response.success) {
          // Check if duplicate exists AND if it's not the current quiz (for editing)
          const isDuplicate = response.exists && (!currentQuizId || response.data?.id !== parseInt(currentQuizId));
          setIsDuplicate(isDuplicate);
          setDuplicateQuizId(isDuplicate ? response.data?.id : null);
        } else {
          setError(response.message || 'Failed to check title');
          setIsDuplicate(false);
          setDuplicateQuizId(null);
        }
      } catch (err) {
        // Ignore abort errors (they're expected when component unmounts)
        if (err.name !== 'AbortError') {
          console.error('Error checking quiz title:', err);
          setError('Failed to check title availability');
          setIsDuplicate(false);
          setDuplicateQuizId(null);
        }
      } finally {
        setIsChecking(false);
      }
    }, delay);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [title, delay, currentQuizId]);

  return {
    isDuplicate,
    isChecking,
    error,
    duplicateQuizId
  };
}

export default useDebouncedTitleValidation;
